import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, apiError, peso } from "@/lib/api";
import { propertyTypeLabel } from "@/lib/taxonomy";
import { saveReferralResume } from "@/lib/referralResume";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, Loader2, MapPin, Wallet, BedDouble, Lock, ShieldCheck, CheckCircle2, Home, Plus } from "lucide-react";
import { toast } from "sonner";

// One Referral creation engine, two entry modes (both POST /api/referrals/client):
//  • FROM_NEED  (CP4): Buyer Need preselected → choose recipient professional.
//  • FROM_PRO   (CP5): recipient preselected (Profile/Connections) → choose owned Buyer Need.
// Client PII is opt-in and stays locked until acceptance. Nothing here is duplicated per entry.
export function ReferClientModal({ need, recipient, initialNeedId, open, onOpenChange, onCreated, entrySurface }) {
  const nav = useNavigate();
  const loc = useLocation();
  const fromPro = !!recipient && !need;
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [pros, setPros] = useState([]);
  const [pro, setPro] = useState(null);
  const [ownedNeeds, setOwnedNeeds] = useState(null);
  const [selNeed, setSelNeed] = useState(null);
  const [leads, setLeads] = useState([]);
  const [srcLead, setSrcLead] = useState(null);
  const [f, setF] = useState({ client_name: "", client_mobile: "", client_email: "", note: "" });
  const [share, setShare] = useState({ name: false, mobile: false, email: false });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const reset = useCallback(() => {
    setQ(""); setPros([]); setSrcLead(null);
    setF({ client_name: "", client_mobile: "", client_email: "", note: "" });
    setShare({ name: false, mobile: false, email: false });
  }, []);

  // Initialise selection from props whenever the modal opens.
  useEffect(() => {
    if (!open) return;
    setPro(recipient || null);
    setSelNeed(need || null);
  }, [open, recipient, need]);

  // FROM_NEED: search eligible recipient professionals (debounced).
  useEffect(() => {
    if (!open || fromPro) return;
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/referrals/eligible-professionals", { params: { q } }); setPros(data || []); }
      catch { setPros([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, open, fromPro]);

  // FROM_PRO: load the current professional's own Buyer Needs eligible for referral.
  useEffect(() => {
    if (!open || !fromPro) return;
    (async () => {
      try {
        const { data } = await api.get("/me/buyer-requirements");
        const list = (data || []).filter((b) => (b.origin || "PROFESSIONAL_REPRESENTED") !== "SELF" && ["Active", "Paused"].includes(b.status));
        setOwnedNeeds(list);
        if (initialNeedId) { const m = list.find((b) => b.id === initialNeedId); if (m) setSelNeed(m); }
      } catch { setOwnedNeeds([]); }
    })();
  }, [open, fromPro, initialNeedId]);

  // Owned Leads tied to the selected Buyer Need (dual-source contact).
  useEffect(() => {
    if (!open || !selNeed) { setLeads([]); return; }
    (async () => {
      try {
        const { data } = await api.get("/leads");
        setLeads((data?.leads || data || []).filter((l) => l.buyer_requirement_id === selNeed.id));
      } catch { setLeads([]); }
    })();
  }, [open, selNeed]);

  const applyLead = (l) => {
    setSrcLead(l);
    setF((p) => ({ ...p, client_name: l.contact_name || "", client_mobile: l.contact_mobile || "", client_email: l.contact_email || "" }));
  };

  // Disable a sharing toggle when its value is empty (no meaningless authorization).
  useEffect(() => {
    setShare((s) => ({
      name: s.name && !!f.client_name.trim(),
      mobile: s.mobile && !!f.client_mobile.trim(),
      email: s.email && !!f.client_email.trim(),
    }));
  }, [f.client_name, f.client_mobile, f.client_email]);

  if (!need && !recipient) return null;

  const needBudget = (b) => b.listing_type === "Unit Hosting"
    ? `${peso(b.nightly_budget_min)}–${peso(b.nightly_budget_max)} / night`
    : `${peso(b.budget_min)}–${peso(b.budget_max)}`;
  const sharedLabels = [share.name && "Name", share.mobile && "Mobile", share.email && "Email"].filter(Boolean);

  const createNeed = () => {
    saveReferralResume({ recipient: pro, returnTo: loc.pathname + loc.search });
    onOpenChange(false);
    nav("/have-buyer?resume=referral");
  };

  const submit = async () => {
    if (!pro) { toast.error("Select a professional to refer to."); return; }
    if (!selNeed) { toast.error("Select a Buyer Need to refer."); return; }
    setBusy(true);
    try {
      await api.post("/referrals/client", {
        professional_id: pro.id,
        buyer_requirement_id: selNeed.id,
        note: f.note.trim(),
        source_lead_id: srcLead?.id || null,
        client_name: f.client_name.trim(),
        client_mobile: f.client_mobile.trim(),
        client_email: f.client_email.trim(),
        share_client_name: share.name,
        share_client_mobile: share.mobile,
        share_client_email: share.email,
        entry_surface: entrySurface || (need ? "BUYER_NEED" : "PROFILE"),
      });
      toast.success("Referral sent");
      onOpenChange(false); reset();
      onCreated ? onCreated() : nav("/referrals?tab=sent");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) toast.error("You already have an active referral for this Buyer Need with this professional.");
      else if (status === 429) toast.error("Daily referral limit reached. Please try again later.");
      else if (status === 403) toast.error("You can only refer your own Buyer Need.");
      else toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const showRest = !!pro && !!selNeed;

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0 gap-0" data-testid="refer-client-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100"><DialogTitle>Refer a Client</DialogTitle></DialogHeader>
        <div className="space-y-5 overflow-y-auto px-6 py-5 flex-1">

          {/* Recipient — preselected (FROM_PRO) or searchable (FROM_NEED) */}
          <div>
            <Label>Refer to professional</Label>
            {pro ? (
              <div className="mt-1.5 flex items-center gap-2.5 rounded-xl border-2 border-primary bg-primary/[0.04] px-3 py-2" data-testid="refer-client-selected">
                <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                {pro.avatar && <img src={pro.avatar} alt="" className="h-8 w-8 rounded-full object-cover" />}
                <span className="min-w-0">
                  <span className="flex items-center gap-1 text-sm font-semibold truncate">{pro.name}<ShieldCheck className="h-3.5 w-3.5 text-emerald-500 shrink-0" /></span>
                  <span className="block text-xs text-slate-400 truncate">{pro.role_label}{pro.organization_name ? ` · ${pro.organization_name}` : ""}{pro.city ? ` · ${pro.city}` : ""}</span>
                </span>
                {!fromPro && <button type="button" onClick={() => setPro(null)} className="ml-auto text-xs text-slate-500 hover:text-slate-800">Change</button>}
              </div>
            ) : (
              <div className="mt-1.5">
                <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input data-testid="refer-client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, organization or city…" className="pl-9" /></div>
                <ul className="mt-1 max-h-[248px] overflow-y-auto rounded-xl border border-border divide-y" data-testid="refer-client-pro-list">
                  {pros.map((p) => (
                    <li key={p.id}><button type="button" data-testid={`refer-client-pro-${p.id}`} onClick={() => setPro(p)} className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-slate-50">
                      {p.avatar && <img src={p.avatar} alt="" className="h-8 w-8 rounded-full object-cover shrink-0" />}
                      <span className="min-w-0 flex-1"><span className="flex items-center gap-1 text-sm font-medium truncate">{p.name}<ShieldCheck className="h-3 w-3 text-emerald-500 shrink-0" /></span>
                        <span className="block text-xs text-slate-400 truncate">{p.role_label}{p.organization_name ? ` · ${p.organization_name}` : ""}{p.city ? ` · ${p.city}` : ""}</span></span>
                    </button></li>
                  ))}
                  {!pros.length && <li className="px-3 py-2 text-xs text-slate-400">No matching professionals.</li>}
                </ul>
              </div>
            )}
          </div>

          {/* FROM_PRO: choose which owned Buyer Need to refer */}
          {fromPro && !selNeed && (
            <div data-testid="refer-client-need-step">
              <Label>Select a Buyer Need</Label>
              {ownedNeeds === null ? <p className="text-xs text-slate-400 mt-2">Loading your Buyer Needs…</p>
                : ownedNeeds.length === 0 ? (
                  <div className="mt-2 rounded-xl border border-dashed border-slate-300 p-5 text-center" data-testid="refer-client-no-needs">
                    <Home className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm font-medium text-slate-600">No Buyer Needs available to refer</p>
                    <p className="text-xs text-slate-400 mt-1">Create a Buyer Need first to capture your client's property requirements.</p>
                    <Button data-testid="refer-client-create-need" onClick={createNeed} className="mt-3 h-9 rounded-lg bg-[#0F2C59] text-white"><Plus className="h-4 w-4 mr-1" /> Create Buyer Need</Button>
                  </div>
                ) : (
                  <>
                    <ul className="mt-1.5 max-h-[248px] overflow-y-auto rounded-xl border border-border divide-y" data-testid="refer-client-need-list">
                      {ownedNeeds.map((b) => (
                        <li key={b.id}><button type="button" data-testid={`refer-client-need-${b.id}`} onClick={() => setSelNeed(b)} className="w-full px-3 py-2.5 text-left hover:bg-slate-50">
                          <span className="block text-sm font-semibold text-slate-800 truncate">{b.buyer_alias || "Client"}</span>
                          <span className="block text-xs text-slate-500 truncate">{propertyTypeLabel(b.property_type)} · {b.listing_type === "Unit Hosting" ? "Stay" : b.listing_type} · {(b.cities || []).join(", ")} · {needBudget(b)}</span>
                        </button></li>
                      ))}
                    </ul>
                    <button type="button" data-testid="refer-client-create-need-inline" onClick={createNeed} className="mt-2 text-xs text-[#0F2C59] font-semibold hover:underline inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Create a new Buyer Need</button>
                  </>
                )}
            </div>
          )}

          {/* Opportunity snapshot (once a Need is chosen) */}
          {selNeed && (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4" data-testid="refer-client-snapshot">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Client is looking for</p>
                {fromPro && <button type="button" onClick={() => setSelNeed(null)} className="text-xs text-slate-500 hover:text-slate-800">Change</button>}
              </div>
              <p className="text-sm font-semibold text-slate-900 mt-1.5">{propertyTypeLabel(selNeed.property_type)} · {selNeed.listing_type === "Unit Hosting" ? "Stay" : selNeed.listing_type}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-slate-600">
                {selNeed.cities?.length ? <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{selNeed.cities.join(", ")}</span> : null}
                <span className="flex items-center gap-1"><Wallet className="h-3.5 w-3.5" />{needBudget(selNeed)}</span>
                {selNeed.min_bedrooms ? <span className="flex items-center gap-1"><BedDouble className="h-3.5 w-3.5" />{selNeed.min_bedrooms}+ bedrooms</span> : null}
              </div>
              {selNeed.public_description && <p className="text-sm text-slate-500 mt-2">{selNeed.public_description}</p>}
            </div>
          )}

          {showRest && <>
            {/* Client contact (dual source) */}
            <div>
              <Label>Client contact <span className="text-slate-400 font-normal">(optional)</span></Label>
              <p className="text-xs text-slate-500 mt-1">Sharing is optional. Add any details you'd like to offer — you choose below what unlocks after the professional accepts.</p>
              {leads.length > 0 && (
                <div className="mt-2" data-testid="refer-client-lead-source">
                  <p className="text-xs font-medium text-slate-500 mb-1.5">Use contact from an existing Lead</p>
                  <div className="flex flex-wrap gap-1.5">
                    {leads.slice(0, 4).map((l) => (
                      <button key={l.id} type="button" onClick={() => applyLead(l)}
                        className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border ${srcLead?.id === l.id ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border text-slate-600"}`}>
                        {srcLead?.id === l.id && <CheckCircle2 className="h-3 w-3" />}{l.contact_name || "Client contact"}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 gap-2.5 mt-2">
                <Input data-testid="refer-client-name" value={f.client_name} onChange={(e) => { set("client_name", e.target.value); setSrcLead(null); }} placeholder="Client name" />
                <div className="grid grid-cols-2 gap-2.5">
                  <Input data-testid="refer-client-mobile" value={f.client_mobile} onChange={(e) => { set("client_mobile", e.target.value); setSrcLead(null); }} placeholder="Mobile +63 9XX…" />
                  <Input data-testid="refer-client-email" value={f.client_email} onChange={(e) => { set("client_email", e.target.value); setSrcLead(null); }} placeholder="Email" />
                </div>
              </div>
            </div>

            {/* Sharing controls */}
            <div className="rounded-xl border border-slate-200 p-4">
              <p className="text-sm font-semibold text-slate-900 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5 text-slate-500" /> Client contact sharing</p>
              <p className="text-xs text-slate-500 mt-1 mb-3">Contact you authorize becomes visible only after the receiving professional accepts.</p>
              <div className="space-y-2">
                {[["name", "Share client name", f.client_name], ["mobile", "Share mobile number", f.client_mobile], ["email", "Share email address", f.client_email]].map(([k, label, val]) => (
                  <label key={k} className={`flex items-center gap-2.5 text-sm ${val.trim() ? "text-slate-700 cursor-pointer" : "text-slate-300 cursor-not-allowed"}`}>
                    <Checkbox data-testid={`refer-share-${k}`} disabled={!val.trim()} checked={share[k]} onCheckedChange={(v) => setShare((s) => ({ ...s, [k]: !!v }))} />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Referral note */}
            <div>
              <Label>Referral note <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Textarea data-testid="refer-client-note" value={f.note} onChange={(e) => set("note", e.target.value)} rows={3} maxLength={1000} className="mt-1.5" placeholder="Professional context for the receiving agent…" />
              <p className="text-xs text-slate-400 mt-1">Do not include private client contact information here.</p>
            </div>

            {/* Review */}
            <div className="rounded-xl bg-[#0F2C59]/[0.04] border border-[#0F2C59]/10 p-3 text-sm" data-testid="refer-client-review">
              <div className="flex justify-between"><span className="text-slate-500">Recipient</span><span className="font-medium text-slate-800">{pro?.name || "—"}</span></div>
              <div className="flex justify-between mt-1"><span className="text-slate-500">Shared after acceptance</span><span className="font-medium text-slate-800">{sharedLabels.length ? sharedLabels.join(", ") : "None"}</span></div>
            </div>
          </>}
        </div>
        <DialogFooter className="px-6 py-4 border-t border-slate-100">
          <Button data-testid="refer-client-submit" onClick={submit} disabled={busy || !pro || !selNeed} className="bg-[#0F2C59] text-white hover:bg-[#0F2C59]/90 font-semibold rounded-xl">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Referral"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
